import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Sparkles, X, Command } from 'lucide-react';
import { PatternOption } from '../../types/interiorConfig';
import { useTheme } from '../styles/ThemeContext';
export type PatternMode = 'bar' | 'modal' | 'pane';
interface PatternProps {
  itemName: string;
  optionName: string;
  patternOptions: PatternOption[];
  selectedPatternId: string | null;
  onSelectPattern: (pattern: PatternOption) => void;
  onClose?: () => void;
}
function getEffectiveId(patternOptions: PatternOption[], selectedPatternId: string | null) {
  return selectedPatternId || patternOptions.find(p => p.selected)?.id || patternOptions[0]?.id || null;
}
/* ------------------------------------------------------------------ */
/* VARIANT 1 — BAR (floating pill above the gallery, larger thumbs)   */
/* ------------------------------------------------------------------ */
export function PatternBar({
  optionName,
  patternOptions,
  selectedPatternId,
  onSelectPattern
}: PatternProps) {
  const {
    isDark
  } = useTheme();
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const effectiveId = getEffectiveId(patternOptions, selectedPatternId);
  const activeId = hoveredId || effectiveId;
  const activePattern = patternOptions.find(p => p.id === activeId);
  const displayLabel = activePattern ? activePattern.priceModifier ? `${activePattern.name} · +$${activePattern.priceModifier.toLocaleString()}` : `${activePattern.name} · Included` : 'Choose pattern';
  return <motion.div initial={{
    opacity: 0,
    y: 8,
    scale: 0.97
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} exit={{
    opacity: 0,
    y: 8,
    scale: 0.97
  }} transition={{
    duration: 0.2,
    ease: [0.22, 1, 0.36, 1]
  }} className={`flex items-center gap-3 px-5 py-3 rounded-full backdrop-blur-xl border shadow-2xl pointer-events-auto ${isDark ? 'bg-slate-800/90 border-white/10' : 'bg-white/90 border-gray-200 shadow-lg'}`} onClick={e => e.stopPropagation()} data-id="element-1788">
      <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-1789">
        <Sparkles className={`w-3.5 h-3.5 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1790" />
        <span className={`text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${isDark ? 'text-white/50' : 'text-gray-400'}`} data-id="element-1791">
          {optionName} Pattern
        </span>
      </div>
      <div className={`w-px h-6 ${isDark ? 'bg-white/10' : 'bg-gray-200'}`} data-id="element-1792" />
      <div className="flex items-center gap-2" data-id="element-1793">
        {patternOptions.map((pattern, i) => {
        const isSelected = effectiveId === pattern.id;
        return <motion.button key={pattern.id} initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          delay: i * 0.03,
          duration: 0.18
        }} whileHover={{
          scale: 1.08
        }} whileTap={{
          scale: 0.95
        }} onClick={() => onSelectPattern(pattern)} onMouseEnter={() => setHoveredId(pattern.id)} onMouseLeave={() => setHoveredId(null)} className={`relative w-12 h-12 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${isSelected ? isDark ? 'border-cyan-400 shadow-[0_0_12px_rgba(6,182,212,0.5)]' : 'border-teal-500 shadow-[0_0_12px_rgba(20,184,166,0.4)]' : isDark ? 'border-white/20 hover:border-white/40' : 'border-gray-300 hover:border-gray-400'}`} aria-label={pattern.name} title={pattern.name} data-id="element-1794">
              <img src={pattern.thumbnail} alt={pattern.name} className="w-full h-full object-cover bg-white" data-id="element-1795" />
              {isSelected && <div className={`absolute inset-0 flex items-center justify-center ${isDark ? 'bg-cyan-500/30' : 'bg-teal-500/30'}`} data-id="element-1796">
                  <div className={`rounded-full p-1 ${isDark ? 'bg-cyan-500' : 'bg-teal-500'}`} data-id="element-1797">
                    <Check className="w-3 h-3 text-white" strokeWidth={3} data-id="element-1798" />
                  </div>
                </div>}
            </motion.button>;
      })}
      </div>
      <div className={`w-px h-6 ${isDark ? 'bg-white/10' : 'bg-gray-200'}`} data-id="element-1799" />
      <AnimatePresence mode="wait" data-id="element-1800">
        <motion.span key={displayLabel} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.1
      }} className={`text-xs font-medium whitespace-nowrap min-w-[140px] ${isDark ? 'text-white/70' : 'text-gray-600'}`} data-id="element-1801">
          {displayLabel}
        </motion.span>
      </AnimatePresence>
    </motion.div>;
}
/* ------------------------------------------------------------------ */
/* VARIANT 2 — MODAL (centered, large preview tiles, Roomvo-style)    */
/* ------------------------------------------------------------------ */
export function PatternModal({
  optionName,
  patternOptions,
  selectedPatternId,
  onSelectPattern,
  onClose
}: PatternProps) {
  const {
    isDark
  } = useTheme();
  const effectiveId = getEffectiveId(patternOptions, selectedPatternId);
  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-[80] flex items-center justify-center p-6 pointer-events-auto" onClick={onClose} data-id="element-1802">
      {/* Backdrop */}
      <div className={`absolute inset-0 backdrop-blur-md ${isDark ? 'bg-slate-900/60' : 'bg-gray-900/40'}`} data-id="element-1803" />

      {/* Modal */}
      <motion.div initial={{
      scale: 0.92,
      y: 12,
      opacity: 0
    }} animate={{
      scale: 1,
      y: 0,
      opacity: 1
    }} exit={{
      scale: 0.92,
      y: 12,
      opacity: 0
    }} transition={{
      duration: 0.25,
      ease: [0.22, 1, 0.36, 1]
    }} className={`relative w-full max-w-3xl rounded-3xl border shadow-2xl overflow-hidden ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white border-gray-200'}`} onClick={e => e.stopPropagation()} data-id="element-1804">
        {/* Header */}
        <div className={`flex items-start justify-between px-8 py-6 border-b ${isDark ? 'border-white/10' : 'border-gray-100'}`} data-id="element-1805">
          <div className="flex items-start gap-3" data-id="element-1806">
            <div className={`p-2 rounded-xl flex-shrink-0 ${isDark ? 'bg-cyan-500/15' : 'bg-teal-500/10'}`} data-id="element-1807">
              <Sparkles className={`w-5 h-5 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1808" />
            </div>
            <div data-id="element-1809">
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1810">
                Choose a Pattern
              </h2>
              <p className={`text-sm mt-0.5 ${isDark ? 'text-white/50' : 'text-gray-500'}`} data-id="element-1811">
                {optionName} — {patternOptions.length} patterns available
              </p>
            </div>
          </div>
          {onClose && <button onClick={onClose} className={`p-2 rounded-full transition-colors flex-shrink-0 ${isDark ? 'text-white/40 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`} data-id="element-1812">
              <X className="w-5 h-5" data-id="element-1813" />
            </button>}
        </div>

        {/* Pattern grid */}
        <div className="px-8 py-6 max-h-[60vh] overflow-y-auto custom-scrollbar" data-id="element-1814">
          <div className="grid grid-cols-3 gap-4" data-id="element-1815">
            {patternOptions.map((pattern, i) => {
            const isSelected = effectiveId === pattern.id;
            return <motion.button key={pattern.id} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.04,
              duration: 0.2
            }} whileHover={{
              y: -2
            }} whileTap={{
              scale: 0.98
            }} onClick={() => onSelectPattern(pattern)} className={`group relative flex flex-col items-stretch gap-2 p-3 rounded-2xl border-2 transition-all text-left ${isSelected ? isDark ? 'border-cyan-500 bg-cyan-500/10 shadow-[0_0_20px_rgba(6,182,212,0.25)]' : 'border-teal-500 bg-teal-50 shadow-[0_0_20px_rgba(20,184,166,0.2)]' : isDark ? 'border-white/10 hover:border-white/30 bg-white/5' : 'border-gray-200 hover:border-gray-300 bg-gray-50'}`} data-id="element-1816">
                  <div className={`relative aspect-square rounded-xl overflow-hidden border ${isDark ? 'border-white/10 bg-white' : 'border-gray-200 bg-white'}`} data-id="element-1817">
                    <img src={pattern.thumbnail} alt={pattern.name} className="w-full h-full object-cover" data-id="element-1818" />
                    {isSelected && <div className={`absolute top-2 right-2 rounded-full p-1 ${isDark ? 'bg-cyan-500' : 'bg-teal-500'}`} data-id="element-1819">
                        <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} data-id="element-1820" />
                      </div>}
                  </div>
                  <div className="flex flex-col gap-0.5 px-1" data-id="element-1821">
                    <span className={`text-sm font-semibold ${isSelected ? isDark ? 'text-cyan-300' : 'text-teal-700' : isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1822">
                      {pattern.name}
                    </span>
                    <span className={`text-xs font-bold ${!pattern.priceModifier ? isDark ? 'text-emerald-400' : 'text-emerald-600' : isDark ? 'text-white/50' : 'text-gray-500'}`} data-id="element-1823">
                      {pattern.priceModifier ? `+$${pattern.priceModifier.toLocaleString()}` : 'Included'}
                    </span>
                  </div>
                </motion.button>;
          })}
          </div>
        </div>
      </motion.div>
    </motion.div>;
}
/* ------------------------------------------------------------------ */
/* VARIANT 3 — PANE (left-side slide-in, full height, anchored)       */
/* ------------------------------------------------------------------ */
export function PatternPane({
  optionName,
  patternOptions,
  selectedPatternId,
  onSelectPattern,
  onClose
}: PatternProps) {
  const {
    isDark
  } = useTheme();
  const effectiveId = getEffectiveId(patternOptions, selectedPatternId);
  if (typeof document === 'undefined') return null;
  return createPortal(<motion.div initial={{
    opacity: 0,
    x: 40
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: 40
  }} transition={{
    duration: 0.3,
    ease: [0.22, 1, 0.36, 1]
  }} className={`fixed right-20 top-4 bottom-44 w-[230px] rounded-2xl border shadow-2xl backdrop-blur-2xl overflow-hidden pointer-events-auto flex flex-col ${isDark ? 'bg-slate-900/80 border-white/10' : 'bg-white/85 border-white/40'}`} style={{
    zIndex: 2147483647
  }} onClick={e => e.stopPropagation()} data-id="element-1824">
      {/* Header */}
      <div className={`flex items-start justify-between px-5 py-4 border-b flex-shrink-0 ${isDark ? 'border-white/10' : 'border-gray-100'}`} data-id="element-1825">
        <div className="flex items-start gap-2.5 min-w-0" data-id="element-1826">
          <div className={`p-1.5 rounded-lg flex-shrink-0 ${isDark ? 'bg-cyan-500/15' : 'bg-teal-500/10'}`} data-id="element-1827">
            <Sparkles className={`w-4 h-4 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1828" />
          </div>
          <div className="min-w-0" data-id="element-1829">
            <h3 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1830">
              Pattern
            </h3>
            <p className={`text-xs truncate ${isDark ? 'text-white/50' : 'text-gray-500'}`} title={optionName} data-id="element-1831">
              {optionName}
            </p>
          </div>
        </div>
        {onClose && <button onClick={onClose} className={`p-1.5 rounded-full transition-colors flex-shrink-0 ${isDark ? 'text-white/40 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`} data-id="element-1832">
            <X className="w-4 h-4" data-id="element-1833" />
          </button>}
      </div>

      {/* Pattern list — single column */}
      <div className="flex-1 overflow-y-auto custom-scrollbar p-3" data-id="element-1834">
        <div className="flex flex-col gap-2.5" data-id="element-1835">
          {patternOptions.map((pattern, i) => {
          const isSelected = effectiveId === pattern.id;
          return <motion.button key={pattern.id} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.04,
            duration: 0.2
          }} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => onSelectPattern(pattern)} className={`group relative rounded-xl overflow-hidden border-2 transition-all text-left ${isSelected ? isDark ? 'border-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.3)]' : 'border-teal-500 shadow-[0_0_15px_rgba(20,184,166,0.3)]' : isDark ? 'border-white/10 hover:border-white/30' : 'border-gray-200 hover:border-gray-400'}`} data-id="element-1836">
                <div className="relative aspect-[4/3] overflow-hidden bg-white" data-id="element-1837">
                  <img src={pattern.thumbnail} alt={pattern.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-1838" />
                  {isSelected && <div className={`absolute top-1.5 right-1.5 rounded-full p-0.5 ${isDark ? 'bg-cyan-500' : 'bg-teal-500'}`} data-id="element-1839">
                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} data-id="element-1840" />
                    </div>}
                  <div className={`absolute bottom-0 inset-x-0 py-1 px-2 backdrop-blur-md ${isDark ? 'bg-black/60' : 'bg-white/80'}`} data-id="element-1841">
                    <span className={`block text-[10px] font-bold text-center uppercase tracking-wider truncate ${isSelected ? isDark ? 'text-cyan-300' : 'text-teal-700' : isDark ? 'text-white/90' : 'text-gray-800'}`} data-id="element-1842">
                      {pattern.name}
                    </span>
                  </div>
                </div>
                {(pattern.priceModifier ?? 0) > 0 && <div className={`px-2 py-1 ${isDark ? 'bg-slate-800/60' : 'bg-white'}`} data-id="element-1843">
                    <span className={`block text-center text-[10px] font-bold ${isDark ? 'text-white/60' : 'text-gray-600'}`} data-id="element-1844">
                      +${pattern.priceModifier!.toLocaleString()}
                    </span>
                  </div>}
              </motion.button>;
        })}
        </div>
      </div>
    </motion.div>, document.body);
}
/* ------------------------------------------------------------------ */
/* SWITCHER — floating button + popup (keyboard P1/P2/P3)             */
/* ------------------------------------------------------------------ */
export function PatternModeSwitcher({
  activeMode,
  onSwitchMode
}: {
  activeMode: PatternMode;
  onSwitchMode: (mode: PatternMode) => void;
}) {
  const {
    isDark
  } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === '1') {
        e.preventDefault();
        onSwitchMode('bar');
      } else if (e.key === '2') {
        e.preventDefault();
        onSwitchMode('modal');
      } else if (e.key === '3') {
        e.preventDefault();
        onSwitchMode('pane');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onSwitchMode]);
  const modes: {
    id: PatternMode;
    label: string;
    desc: string;
    shortcut: string;
  }[] = [{
    id: 'bar',
    label: 'Floating Bar',
    desc: 'Compact pill above the gallery',
    shortcut: '1'
  }, {
    id: 'modal',
    label: 'Modal',
    desc: 'Centered focus dialog',
    shortcut: '2'
  }, {
    id: 'pane',
    label: 'Side Pane',
    desc: 'Slide-in panel from the right',
    shortcut: '3'
  }];
  return <div className="fixed bottom-44 right-6 z-[110] flex flex-col items-end pointer-events-auto" data-id="element-1845">
      <AnimatePresence data-id="element-1846">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 10,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.95
      }} className={`mb-3 w-72 rounded-2xl border shadow-2xl backdrop-blur-xl overflow-hidden ${isDark ? 'bg-slate-900/90 border-white/10' : 'bg-white/90 border-gray-200'}`} data-id="element-1847">
            <div className={`p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-100'}`} data-id="element-1848">
              <div className="flex items-center gap-2 mb-1" data-id="element-1849">
                <Sparkles className={`w-4 h-4 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1850" />
                <h3 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1851">
                  Pattern Selector Mode
                </h3>
              </div>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`} data-id="element-1852">
                Press 1 / 2 / 3 to switch instantly
              </p>
            </div>
            <div className="p-2 space-y-1" data-id="element-1853">
              {modes.map(mode => {
            const isActive = activeMode === mode.id;
            return <button key={mode.id} onClick={() => {
              onSwitchMode(mode.id);
              setIsOpen(false);
            }} className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left ${isActive ? isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-teal-50 text-teal-700' : isDark ? 'hover:bg-white/5 text-white/70 hover:text-white' : 'hover:bg-gray-50 text-gray-600 hover:text-gray-900'}`} data-id="element-1854">
                    <div className="flex-1" data-id="element-1855">
                      <div className="flex items-center justify-between" data-id="element-1856">
                        <span className="text-sm font-medium" data-id="element-1857">
                          {mode.label}
                        </span>
                        {isActive && <Check className="w-3.5 h-3.5" data-id="element-1858" />}
                      </div>
                      <span className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-1859">
                        {mode.desc}
                      </span>
                    </div>
                    <div className={`text-[10px] font-mono opacity-60 px-1.5 py-0.5 rounded border ${isDark ? 'border-white/10' : 'border-gray-200'}`} data-id="element-1860">
                      {mode.shortcut}
                    </div>
                  </button>;
          })}
            </div>
          </motion.div>}
      </AnimatePresence>

      <motion.button whileHover={{
      scale: 1.05
    }} whileTap={{
      scale: 0.95
    }} onClick={() => setIsOpen(!isOpen)} className={`flex items-center gap-2 px-3 py-2.5 rounded-full shadow-lg backdrop-blur-md border transition-all ${isOpen ? isDark ? 'bg-cyan-500 text-white border-cyan-400' : 'bg-teal-600 text-white border-teal-500' : isDark ? 'bg-slate-800/80 text-white border-white/10 hover:bg-slate-700' : 'bg-white/80 text-gray-900 border-gray-200 hover:bg-white'}`} data-id="element-1861">
        <Sparkles className="w-4 h-4" data-id="element-1862" />
        <span className="text-xs font-medium pr-0.5" data-id="element-1863">Pattern</span>
        <div className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${isOpen ? 'bg-black/20' : isDark ? 'bg-white/10' : 'bg-gray-100'}`} data-id="element-1864">
          {activeMode === 'bar' ? '1' : activeMode === 'modal' ? '2' : '3'}
        </div>
      </motion.button>
    </div>;
}
// Backwards-compat alias for previous default export name
export const PatternSlideOut = PatternBar;